import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import ssLogo from "../../../assets/images/sslogo.png";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";

// Icon imports
import { 
  FiSearch, 
  FiFileText, 
  FiGrid, 
  FiCheck, 
  FiX, 
  FiEdit2, 
  FiRefreshCw, 
  FiRotateCcw 
} from "react-icons/fi";

function FormRegistration({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);

  const [editDisabled, setEditDisabled] = useState(true);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const nameInputRef = useRef(null);
  const TypeOfVoucherRef = useRef(null);
  const abbrInputRef = useRef(null);
  const sentryInputRef = useRef(null);
  const agentRef = useRef(null);
  const saveButtonRef = useRef(null);

  const [tableData, setTableData] = useState([]);
  const [agentName, setagentName] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    slno: "",
    name: "",
    typeOfVoucher: "",
    abbr: "",
    agent: "",
    sentry: 0,
    sms: 0,
    grate: 0,
  });

  const typeofVoucherOptions = [
    { label: "RECEIPT", value: "RECEIPT" },
    { label: "REDEEM", value: "REDEEM" },
    { label: "SCHEME RECEIPT", value: "SCHEME RECEIPT" },
    { label: "SCHEME REDEEM", value: "SCHEME REDEEM" },
  ];

  // Fetch all registrations for grid list
  const fetchData = async () => {
    if (!dbCode) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getformregdatas/${dbCode}`
      );
      setTableData(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Salesman/Agent Names for Combobox
  const fetchAgname = async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/schsalesmanNames/${dbCode}`
      );
      const transformedData = (response.data || []).map((item) => ({
        label: item.Name,
        value: item.Auto,
      }));
      setagentName(transformedData);
    } catch (error) {
      console.error("Error fetching agent values:", error.message);
    }
  };

  // Fetch next Serial Number/Entry Number
  const fetchEno = async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/formregEntryno/${dbCode}`
      );
      setFormData((item) => ({
        ...item,
        slno: response.data,
      }));
    } catch (error) {
      console.error("Error fetching entryno:", error);
    }
  };

  useEffect(() => {
    if (dbCode) {
      fetchData();
      fetchAgname();
      fetchEno();
    }
  }, [dbCode]);

  useEffect(() => {
    setTimeout(() => {
      nameInputRef.current?.focus?.();
    }, 150);
  }, []);

  const handleClear = () => {
    setFormData({
      slno: "",
      name: "",
      typeOfVoucher: "",
      abbr: "",
      agent: "",
      sentry: 0,
      sms: 0,
      grate: 0,
    });
    fetchEno();
    fetchData();
    setEditDisabled(true);
    setDeleteDisabled(true);
    setSaveDisabled(false);
    setTimeout(() => {
      nameInputRef.current?.focus?.();
    }, 100);
  };

  const resolveAgentId = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    if (typeof val === "object") {
      val = val.value !== undefined ? val.value : val.label;
    }
    const num = Number(val);
    if (!isNaN(num) && num > 0) return num;

    const str = String(val).trim().toLowerCase();
    const matched = agentName.find(
      (a) =>
        String(a.label || "").trim().toLowerCase() === str ||
        String(a.value || "").trim().toLowerCase() === str
    );
    if (matched && matched.value) {
      return Number(matched.value) || 0;
    }
    return 0;
  };

  const getSalesmanLabel = useCallback((val) => {
    if (val === null || val === undefined || val === "") return "-";
    const str = String(val).trim().toLowerCase();
    const matched = agentName.find(
      (a) =>
        String(a.value || "").trim().toLowerCase() === str ||
        String(a.label || "").trim().toLowerCase() === str
    );
    return matched ? matched.label : String(val);
  }, [agentName]);

  const getVoucherTypeBadge = (voucherType) => {
    switch (voucherType) {
      case "RECEIPT":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "REDEEM":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "SCHEME RECEIPT":
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
      case "SCHEME REDEEM":
        return "bg-purple-50 text-purple-700 border-purple-200";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const handleSelectRecord = (record) => {
    setEditDisabled(false);
    setDeleteDisabled(false);
    setSaveDisabled(true);
    setFormData({
      slno: record.fmrEntryNo,
      name: record.fmrName || "",
      typeOfVoucher: record.fmrTypeOfVoucher || "",
      abbr: record.fmrAbbreviation || "",
      agent: record.fmrAgentId || record.fmrSalesman || "",
      sentry: record.fmrStartingEntryNo !== undefined && record.fmrStartingEntryNo !== null
        ? record.fmrStartingEntryNo 
        : (record.sentry ?? 0),
      sms: record.fmrSMS || 0,
      grate: record.fmrGoldRate || 0,
    });
  };

  const handleSubmit = async () => {
    const resolvedAgent = resolveAgentId(formData.agent);
    if (
      !formData.name ||
      !formData.typeOfVoucher ||
      !formData.abbr ||
      !resolvedAgent ||
      !formData.slno
    ) {
      alert("Please fill all required fields correctly (including Salesman).");
      return;
    }

    setSaveDisabled(true);
    try {
      const payload = {
        ...formData,
        sentry: formData.sentry === "" ? 0 : (parseInt(formData.sentry, 10) || 0),
        agent: resolvedAgent,
      };
      const response = await axios.post(
        `${apiBaseUrl}/main/insertformReg/${dbCode}`,
        payload
      );

      if (response.status === 200) {
        alert("Form Registered Successfully!");
        handleClear();
      } else {
        alert("Failed to register form.");
        setSaveDisabled(false);
      }
    } catch (error) {
      console.error("Error:", error.message);
      alert("Error saving record: " + error.message);
      setSaveDisabled(false);
    }
  };

  const handleUpdate = async () => {
    const resolvedAgent = resolveAgentId(formData.agent);
    if (
      !formData.name ||
      !formData.typeOfVoucher ||
      !formData.abbr ||
      !resolvedAgent ||
      !formData.slno
    ) {
      alert("Please fill all required fields correctly (including Salesman).");
      return;
    }

    if (!window.confirm("Do you want to edit this entry?")) {
      return;
    }

    try {
      const payload = {
        ...formData,
        sentry: formData.sentry === "" ? 0 : (parseInt(formData.sentry, 10) || 0),
        agent: resolvedAgent,
      };
      const response = await axios.post(
        `${apiBaseUrl}/main/updateformReg/${dbCode}`,
        payload
      );

      if (response.status === 200) {
        alert("Form Updated Successfully!");
        handleClear();
      } else {
        alert("Failed to update form.");
      }
    } catch (error) {
      console.error("Error updating form:", error.message);
      alert("Error: " + error.message);
    }
  };

  const handleDelete = async () => {
    if (!formData.slno) return;
    if (!window.confirm("Are you sure you want to delete this form?")) {
      return;
    }

    try {
      const response = await axios.delete(
        `${apiBaseUrl}/main/deleteform/${formData.slno}/${dbCode}`
      );
      if (response.status === 200) {
        alert("Form Deleted Successfully!");
        handleClear();
      } else {
        alert("Failed to delete form.");
      }
    } catch (error) {
      console.error("Error:", error.message);
      alert("Error: " + error.message);
    }
  };

  const [showFindDialog, setShowFindDialog] = useState(false);
  const [findEntryNo, setFindEntryNo] = useState("");

  const handleFind = async () => {
    if (!findEntryNo) return;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/formregfindbyentryno/${findEntryNo}/${dbCode}`
      );

      if (response.status === 200 && response.data && response.data.length > 0) {
        const foundData = response.data[0];
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);

        setFormData({
          slno: foundData.entry,
          name: foundData.name || "",
          typeOfVoucher: foundData.voucher || "",
          abbr: foundData.abbr || "",
          agent: foundData.agent || "",
          sentry: foundData.sentry ?? 0,
          sms: foundData.sms || 0,
          grate: foundData.grate || 0,
        });
        setShowFindDialog(false);
        setFindEntryNo("");
      } else {
        alert("No record found with this SlNo.");
      }
    } catch (error) {
      console.error("Error finding record:", error.message);
      alert("Error retrieving data.");
    }
  };

  const handleKeyDown = (event, nextInputRef) => {
    if (event.key === "Enter" || event.key === "Return") {
      event.preventDefault();
      nextInputRef?.current?.focus?.();
    }
  };

  // Checkbox handlers
  const handleSmsChange = (e) => {
    setFormData({
      ...formData,
      sms: e.target.checked ? 1 : 0,
    });
  };

  const handleGrateChange = (e) => {
    setFormData({
      ...formData,
      grate: e.target.checked ? 1 : 0,
    });
  };

  // Filter existing list
  const filteredTableData = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const needle = searchQuery.toLowerCase().trim();
    return tableData.filter((row) => {
      const formName = (row.fmrName || "").toLowerCase();
      const abbr = (row.fmrAbbreviation || "").toLowerCase();
      const voucher = (row.fmrTypeOfVoucher || "").toLowerCase();
      const salesman = getSalesmanLabel(row.fmrAgentId || row.fmrSalesman).toLowerCase();
      const entryNo = String(row.fmrEntryNo || "");

      return (
        entryNo.includes(needle) ||
        formName.includes(needle) ||
        abbr.includes(needle) ||
        voucher.includes(needle) ||
        salesman.includes(needle)
      );
    });
  }, [tableData, searchQuery, getSalesmanLabel]);

  const updateNavRefs = (refs) => {
    if (refs.saveButtonRef) {
      saveButtonRef.current = refs.saveButtonRef.current;
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-2.5 font-Inter select-none"
      style={{ zIndex: zIndex || 101 }}
    >
      <div className="bg-slate-100 font-normal w-[1000px] max-w-[96vw] h-auto max-h-[92vh] rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 box-border">
        {/* Window Header */}
        <WindowHeader
          title="Form Registration"
          logo={ssLogo}
          onClose={onClose}
          className="!h-7.5 bg-[#1e40af] text-white font-Inter shrink-0"
          titleClassName="!text-xs font-semibold"
          closeBtnClassName="h-4.5 w-4.5 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm flex items-center justify-center text-white"
        />

        {/* Toolbar */}
        <div className="w-full flex-shrink-0 border-b border-slate-200">
          <ActionToolbar
            width="100%"
            height="auto"
            bgColor="#EAF0FC"
            size="small"
            iconSize={15}
            showSave={permissions?.["formRegistration"]?.actions?.Save !== false}
            showPrint={false}
            showClear={true}
            showFind={permissions?.["formRegistration"]?.actions?.Find !== false}
            showEdit={permissions?.["formRegistration"]?.actions?.Edit !== false}
            showDelete={permissions?.["formRegistration"]?.actions?.Delete !== false}
            showExit={true}
            showNavigation={false}
            saveDisabled={formData.slno && editDisabled ? false : saveDisabled}
            editDisabled={editDisabled}
            deleteDisabled={deleteDisabled}
            dataFound={!editDisabled}
            onSave={editDisabled ? handleSubmit : handleUpdate}
            onClear={handleClear}
            onFind={() => setShowFindDialog(true)}
            onEdit={handleUpdate}
            onDelete={handleDelete}
            onExit={onClose}
            onRefsUpdate={updateNavRefs}
          />
        </div>

        {/* Content Body: Vertical Stack (Top: Form, Bottom: 5-Row Table) */}
        <div className="w-full p-3 flex flex-col gap-2.5 overflow-hidden bg-slate-100">
          
          {/* TOP CARD: Form Details (Structured 2-Row Grid Layout) */}
          <div className="bg-white px-4 py-3 rounded-xl border border-slate-200/90 shadow-2xs flex flex-col gap-2.5 shrink-0">
            {/* Header info bar inside card */}
            <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
              <div className="flex items-center gap-2 text-[#0a3899] font-bold text-[11px] uppercase tracking-wider">
                <FiFileText className="text-sm" />
                <span>Form Details</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10.5px] font-bold px-2.5 py-0.5 rounded-full ${
                  editDisabled 
                    ? "bg-blue-50 text-blue-700 border border-blue-200" 
                    : "bg-amber-50 text-amber-800 border border-amber-200"
                }`}>
                  {editDisabled ? "Mode: New Entry" : `Mode: Editing #${formData.slno}`}
                </span>
                {!editDisabled && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-[10.5px] text-slate-500 hover:text-slate-800 font-semibold flex items-center gap-1 underline cursor-pointer"
                  >
                    <FiRotateCcw className="text-[11px]" />
                    <span>Cancel Edit</span>
                  </button>
                )}
              </div>
            </div>

            {/* Row 1: Sl No, Form Name, Type of Voucher, Abbreviation (12 cols) */}
            <div className="grid grid-cols-12 gap-3 items-center">
              {/* Sl No */}
              <div className="col-span-2 sm:col-span-1 flex flex-col gap-0.5">
                <label className="text-[10.5px] font-bold text-slate-600 font-Inter leading-none">
                  Sl No
                </label>
                <input
                  type="text"
                  value={formData.slno}
                  readOnly
                  className="w-full h-[29px] text-center text-xs font-extrabold bg-slate-100 border border-slate-200 rounded-md outline-none text-slate-700 font-mono shadow-2xs"
                />
              </div>

              {/* Form Name */}
              <div className="col-span-10 sm:col-span-4 flex flex-col gap-0.5">
                <label className="text-[10.5px] font-bold text-slate-600 font-Inter leading-none">
                  Form Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  ref={nameInputRef}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onKeyDown={(e) => handleKeyDown(e, TypeOfVoucherRef)}
                  placeholder="Enter form name..."
                  className="w-full h-[29px] px-2.5 text-xs font-medium border border-slate-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/20 bg-white hover:border-slate-400 transition-all"
                />
              </div>

              {/* Type of Voucher */}
              <div className="col-span-6 sm:col-span-4 flex flex-col gap-0.5">
                <label className="text-[10.5px] font-bold text-slate-600 font-Inter leading-none">
                  Type of Voucher <span className="text-red-500">*</span>
                </label>
                <SherComboBox
                  comboRef={TypeOfVoucherRef}
                  options={typeofVoucherOptions}
                  value={formData.typeOfVoucher}
                  onSelect={(opt) => {
                    setFormData((prev) => ({ ...prev, typeOfVoucher: opt.value }));
                  }}
                  onInputChange={(text) => {
                    setFormData((prev) => ({ ...prev, typeOfVoucher: text }));
                  }}
                  placeholder="Select voucher type..."
                  showArrow={true}
                  clearable={false}
                  inputWidth="100%"
                  inputHeight="29px"
                  inputClassName="border border-slate-300 rounded-md focus:border-blue-600 text-xs pl-2.5 font-medium hover:border-slate-400 transition-all bg-white"
                  onKeyDown={(e) => handleKeyDown(e, abbrInputRef)}
                />
              </div>

              {/* Abbreviation (Increased length: 3 columns) */}
              <div className="col-span-6 sm:col-span-3 flex flex-col gap-0.5">
                <label className="text-[10.5px] font-bold text-slate-600 font-Inter leading-none">
                  Abbreviation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  ref={abbrInputRef}
                  value={formData.abbr}
                  onChange={(e) => setFormData({ ...formData, abbr: e.target.value.toUpperCase() })}
                  onKeyDown={(e) => handleKeyDown(e, sentryInputRef)}
                  placeholder="Enter abbreviation..."
                  className="w-full h-[29px] px-2.5 text-xs font-mono font-medium border border-slate-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/20 bg-white hover:border-slate-400 transition-all uppercase"
                />
              </div>
            </div>

            {/* Row 2: Starting Entry No, Salesman, Checkboxes, Action Button (12 cols) */}
            <div className="grid grid-cols-12 gap-3 items-end pt-0.5">
              {/* Starting Entry No */}
              <div className="col-span-4 sm:col-span-2 flex flex-col gap-0.5">
                <label className="text-[10.5px] font-bold text-slate-600 font-Inter leading-none">
                  Start Entry No
                </label>
                <input
                  type="number"
                  min="0"
                  ref={sentryInputRef}
                  value={formData.sentry}
                  onChange={(e) => setFormData({ ...formData, sentry: e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0 })}
                  onKeyDown={(e) => handleKeyDown(e, agentRef)}
                  placeholder="0"
                  className="w-full h-[29px] px-2 text-center text-xs font-mono font-bold border border-slate-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/20 bg-white hover:border-slate-400 transition-all"
                />
              </div>

              {/* Salesman */}
              <div className="col-span-8 sm:col-span-5 flex flex-col gap-0.5">
                <label className="text-[10.5px] font-bold text-slate-600 font-Inter leading-none">
                  Salesman / Agent <span className="text-red-500">*</span>
                </label>
                <SherComboBox
                  comboRef={agentRef}
                  options={agentName}
                  value={formData.agent}
                  onSelect={(opt) => {
                    setFormData((prev) => ({ ...prev, agent: opt ? opt.value : "" }));
                  }}
                  onInputChange={(text) => {
                    setFormData((prev) => {
                      const matched = agentName.find(
                        (a) => String(a.label || "").trim().toLowerCase() === String(text || "").trim().toLowerCase()
                      );
                      return { ...prev, agent: matched ? matched.value : text };
                    });
                  }}
                  placeholder="Enter salesman name..."
                  showArrow={true}
                  clearable={false}
                  inputWidth="100%"
                  inputHeight="29px"
                  inputClassName="border border-slate-300 rounded-md focus:border-blue-600 text-xs pl-2.5 font-medium hover:border-slate-400 transition-all bg-white"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Return") {
                      setTimeout(() => {
                        editDisabled ? handleSubmit() : handleUpdate();
                      }, 50);
                    }
                  }}
                />
              </div>

              {/* Toggles (SMS / Gold Rate) */}
              <div className="col-span-7 sm:col-span-3 flex items-center gap-2 pb-[1px]">
                <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50/80 hover:bg-slate-100 cursor-pointer text-xs font-semibold text-slate-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.sms === 1}
                    onChange={handleSmsChange}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 cursor-pointer"
                  />
                  <span>SMS</span>
                </label>

                <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50/80 hover:bg-slate-100 cursor-pointer text-xs font-semibold text-slate-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.grate === 1}
                    onChange={handleGrateChange}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 cursor-pointer"
                  />
                  <span>Gold Rate</span>
                </label>
              </div>

              {/* Action Button: Save / Update Form */}
              <div className="col-span-5 sm:col-span-2 flex justify-end pb-[1px]">
                <button
                  type="button"
                  onClick={editDisabled ? handleSubmit : handleUpdate}
                  className="w-full h-[29px] bg-[#1e40af] hover:bg-blue-800 active:scale-[0.99] text-white font-bold text-xs rounded-md shadow-xs transition-all cursor-pointer flex items-center justify-center gap-1.5 font-Inter"
                >
                  <FiCheck className="text-xs" />
                  <span>{editDisabled ? "Save Form" : "Update Form"}</span>
                </button>
              </div>
            </div>
          </div>

          {/* BOTTOM CARD: Registered Forms (Data Table: 5 Rows by default, others in scroll) */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-2xs flex flex-col shrink-0 overflow-hidden">
            
            {/* Table Action Bar */}
            <div className="h-9 px-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/80 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center gap-1.5 text-[#0a3899] font-bold text-xs uppercase tracking-wider font-Inter">
                  <FiGrid className="text-xs" />
                  <span>Registered Forms</span>
                </div>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100/70 text-blue-800 font-mono">
                  {filteredTableData.length} {filteredTableData.length === 1 ? "record" : "records"}
                </span>
                {searchQuery.trim() && (
                  <span className="text-[10.5px] text-slate-400 italic">
                    (Filtered from {tableData.length})
                  </span>
                )}
              </div>

              {/* Search Bar */}
              <div className="flex items-center gap-2">
                <div className="relative flex items-center w-64">
                  <FiSearch className="absolute left-2.5 text-slate-400 text-xs pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search name, abbr, type, salesman..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-[27px] pl-7 pr-6 text-xs border border-slate-300 rounded-md outline-none focus:border-blue-600 bg-white hover:border-slate-400 transition-all font-medium placeholder-slate-400"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 text-slate-400 hover:text-slate-600 text-xs cursor-pointer"
                    >
                      <FiX />
                    </button>
                  )}
                </div>

                <button
                  onClick={fetchData}
                  title="Refresh Table"
                  className="h-[27px] w-[27px] rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-blue-600 flex items-center justify-center transition-all cursor-pointer active:scale-95"
                >
                  <FiRefreshCw className={`text-xs ${loading ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>

            {/* Scrollable Table Container: Sized to show exactly 5 rows (Header 30px + 5 * 30px = 180px) */}
            <div className="h-[180px] overflow-y-auto overflow-x-auto report-scroll bg-white">
              <table className="w-full text-left border-collapse text-xs font-Inter">
                <thead className="bg-[#0f56d3] text-white sticky top-0 font-semibold z-10 shadow-xs">
                  <tr className="h-[30px]">
                    <th className="py-1 px-2.5 font-semibold text-[11px] border-r border-blue-400/40 text-center w-[60px] whitespace-nowrap">
                      Sl No
                    </th>
                    <th className="py-1 px-2.5 font-semibold text-[11px] border-r border-blue-400/40 min-w-[170px] whitespace-nowrap">
                      Form Name
                    </th>
                    <th className="py-1 px-2.5 font-semibold text-[11px] border-r border-blue-400/40 w-[140px] min-w-[130px] whitespace-nowrap">
                      Abbreviation
                    </th>
                    <th className="py-1 px-2.5 font-semibold text-[11px] border-r border-blue-400/40 text-center w-[75px] whitespace-nowrap">
                      Start No
                    </th>
                    <th className="py-1 px-2.5 font-semibold text-[11px] border-r border-blue-400/40 w-[150px] whitespace-nowrap">
                      Type Of Voucher
                    </th>
                    <th className="py-1 px-2.5 font-semibold text-[11px] border-r border-blue-400/40 min-w-[150px] whitespace-nowrap">
                      Salesman
                    </th>
                    <th className="py-1 px-2 font-semibold text-[11px] border-r border-blue-400/40 text-center w-[65px] whitespace-nowrap">
                      SMS
                    </th>
                    <th className="py-1 px-2 font-semibold text-[11px] border-r border-blue-400/40 text-center w-[80px] whitespace-nowrap">
                      Gold Rate
                    </th>
                    <th className="py-1 px-2 font-semibold text-[11px] text-center w-[70px] whitespace-nowrap">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                  {filteredTableData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="h-[150px] text-center text-xs text-slate-400 italic bg-white">
                        {loading ? "Loading forms..." : "No registered forms found"}
                      </td>
                    </tr>
                  ) : (
                    filteredTableData.map((row, idx) => {
                      const isSelected = String(row.fmrEntryNo) === String(formData.slno);
                      const rowBg = isSelected
                        ? "bg-blue-100/80 text-blue-900 font-semibold ring-1 ring-inset ring-blue-400/60"
                        : idx % 2 === 0
                        ? "bg-white"
                        : "bg-slate-50/70";

                      return (
                        <tr
                          key={row.fmrEntryNo}
                          onClick={() => handleSelectRecord(row)}
                          className={`h-[30px] border-b border-slate-200 hover:bg-blue-50/60 transition-colors duration-150 cursor-pointer ${rowBg}`}
                        >
                          <td className="py-1 px-2.5 text-center border-r border-slate-200/80 font-mono font-bold text-slate-700 whitespace-nowrap">
                            {row.fmrEntryNo}
                          </td>
                          <td className="py-1 px-2.5 border-r border-slate-200/80 font-semibold text-slate-900 whitespace-nowrap">
                            {row.fmrName}
                          </td>
                          <td className="py-1 px-2.5 border-r border-slate-200/80 font-mono text-slate-700 uppercase whitespace-nowrap">
                            {row.fmrAbbreviation}
                          </td>
                          <td className="py-1 px-2.5 text-center border-r border-slate-200/80 font-mono font-bold text-slate-700 whitespace-nowrap">
                            {row.fmrStartingEntryNo ?? row.sentry ?? 0}
                          </td>
                          <td className="py-1 px-2.5 border-r border-slate-200/80 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded text-[10.5px] font-bold border ${getVoucherTypeBadge(row.fmrTypeOfVoucher)}`}>
                              {row.fmrTypeOfVoucher}
                            </span>
                          </td>
                          <td className="py-1 px-2.5 border-r border-slate-200/80 text-slate-700 whitespace-nowrap">
                            {getSalesmanLabel(row.fmrAgentId || row.fmrSalesman)}
                          </td>
                          <td className="py-1 px-2 text-center border-r border-slate-200/80 whitespace-nowrap">
                            {row.fmrSMS === 1 ? (
                              <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Yes
                              </span>
                            ) : (
                              <span className="text-slate-400 text-[10.5px]">No</span>
                            )}
                          </td>
                          <td className="py-1 px-2 text-center border-r border-slate-200/80 whitespace-nowrap">
                            {row.fmrGoldRate === 1 ? (
                              <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                Yes
                              </span>
                            ) : (
                              <span className="text-slate-400 text-[10.5px]">No</span>
                            )}
                          </td>
                          <td className="py-1 px-2 text-center whitespace-nowrap">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectRecord(row);
                              }}
                              className="px-2 py-0.5 rounded text-[10.5px] font-bold text-blue-600 hover:bg-blue-100 flex items-center gap-1 mx-auto transition-colors"
                            >
                              <FiEdit2 className="text-[10px]" />
                              <span>Select</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Footer Status */}
            <div className="h-7 px-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500 font-medium shrink-0">
              <span className="text-slate-400 text-[10.5px]">
                Showing 5 rows by default • Scroll to view more
              </span>
              <span className="font-bold text-slate-600 font-mono">
                Total: {filteredTableData.length}
              </span>
            </div>
          </div>
        </div>

        {/* Find Dialog Popup */}
        {showFindDialog && (
          <div className="fixed inset-0 flex items-center justify-center bg-slate-900/55 z-[10000] backdrop-blur-2xs animate-fade-in font-Inter">
            <div className="bg-white p-4 rounded-xl shadow-2xl w-[360px] flex flex-col gap-3.5 border border-slate-200 relative overflow-hidden">
              {/* Close Button Row */}
              <div className="flex items-center justify-between w-full border-b border-slate-100 pb-2">
                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <FiSearch className="text-blue-600" />
                  <span>Find Form Registration</span>
                </span>
                <button
                  onClick={() => {
                    setShowFindDialog(false);
                    setFindEntryNo("");
                  }}
                  className="w-5 h-5 rounded-sm bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors cursor-pointer"
                >
                  <FiX className="text-xs" />
                </button>
              </div>

              {/* Input field */}
              <div className="relative flex items-center w-full">
                <input
                  type="text"
                  value={findEntryNo}
                  onChange={(e) => setFindEntryNo(e.target.value)}
                  placeholder="Enter Sl No / Entry number..."
                  className="w-full h-8 px-3 text-xs border border-slate-300 focus:border-blue-600 bg-white rounded-md outline-none focus:ring-1 focus:ring-blue-500/20 text-slate-800 font-semibold"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleFind();
                  }}
                  autoFocus
                />
              </div>

              {/* Bottom buttons */}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => {
                    setShowFindDialog(false);
                    setFindEntryNo("");
                  }}
                  className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-md transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFind}
                  className="px-4 py-1 bg-[#1e40af] hover:bg-blue-800 text-white text-xs font-bold rounded-md transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                >
                  <FiSearch className="text-xs" />
                  <span>Find</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FormRegistration;
